import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { DynamicApiService, DynamicForm } from '../dynamic/dynamic-api.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CreateInsuranceProductVersionPayload,
  InsuranceClauseItem,
  InsuranceCoverageItem,
  InsuranceApiService,
  InsuranceProductVersionDetail,
  InsuranceUwRule,
  InsuranceQuestionItem,
  PutInsuranceUwRuleItemPayload,
  UpdateInsuranceProductVersionPayload
} from './insurance-api.service';

@Component({
  standalone: true,
  selector: 'ain-insurance-product-version-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    RouterLink,
    FormsModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    CheckboxModule,
    TextareaModule,
    PartySelectorComponent,
    ButtonModule
  ],
  template: `
    <ain-page [title]="t('insurance.productVersionDetail.title')" [description]="t('insurance.productVersionDetail.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (isCreateMode()) {
        <section class="card">
          <a class="back-link" [routerLink]="['/insurance/products', productId()]">{{ t('catalog.action.backToList') }}</a>
          <h3>{{ t('insurance.productVersions.actions.create') }}</h3>
          <form class="grid" (ngSubmit)="saveCreate()">
            <label>
              <span>{{ t('insurance.products.fields.legalEntityId') }}</span>
              <ain-party-selector [(ngModel)]="createForm.legalEntityId" name="legalEntityId" />
            </label>
            <label>
              <span>{{ t('insurance.productVersions.fields.versionNumber') }}</span>
              <p-inputNumber [(ngModel)]="createForm.versionNumber" [min]="1" name="versionNumber"></p-inputNumber>
            </label>
            <label>
              <span>{{ t('insurance.productVersions.fields.minPremium') }}</span>
              <p-inputNumber [(ngModel)]="createForm.minPremium" [min]="0" name="minPremium"></p-inputNumber>
            </label>
            <label>
              <span>{{ t('insurance.productVersions.fields.maxSumInsured') }}</span>
              <p-inputNumber [(ngModel)]="createForm.maxSumInsured" [min]="0" name="maxSumInsured"></p-inputNumber>
            </label>
            <label>
              <span>{{ t('insurance.productVersions.fields.uwFormDefinitionId') }}</span>
              <p-select [options]="uwFormOptions()" optionLabel="label" optionValue="value" [(ngModel)]="createForm.uwFormDefinitionId" name="createUwFormDefinitionId" appendTo="body"></p-select>
            </label>
            <label class="wide">
              <span>{{ t('insurance.productVersions.fields.terms') }}</span>
              <div class="terms-grid">
                <label>
                  <span>{{ t('insurance.productVersions.fields.termsTitle') }}</span>
                  <input pInputText [(ngModel)]="createTermsForm.title" name="createTermsTitle" />
                </label>
                <label>
                  <span>{{ t('insurance.productVersions.fields.waitingPeriodDays') }}</span>
                  <p-inputNumber [(ngModel)]="createTermsForm.waitingPeriodDays" [min]="0" name="createWaitingPeriodDays"></p-inputNumber>
                </label>
                <label class="wide">
                  <span>{{ t('insurance.productVersions.fields.termsSummary') }}</span>
                  <textarea pTextarea rows="3" [(ngModel)]="createTermsForm.summary" name="createTermsSummary"></textarea>
                </label>
                <label class="wide">
                  <span>{{ t('insurance.productVersions.fields.cancellationPolicy') }}</span>
                  <textarea pTextarea rows="3" [(ngModel)]="createTermsForm.cancellationPolicy" name="createCancellationPolicy"></textarea>
                </label>
                <label class="wide">
                  <span>{{ t('insurance.productVersions.fields.exclusions') }}</span>
                  <textarea pTextarea rows="3" [(ngModel)]="createTermsForm.exclusions" name="createExclusions"></textarea>
                </label>
              </div>
            </label>
            <div class="actions">
              <button pButton type="submit" [label]="t('common.save')" [disabled]="saving() || !createCanSave()"></button>
            </div>
          </form>
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      } @else if (!version()) {
        <ain-state type="error" [title]="t('insurance.states.notFound')" [message]="t('insurance.states.notFoundMessage')" />
      } @else {
        <section class="card">
          <a class="back-link" [routerLink]="['/insurance/products', productId()]">{{ t('catalog.action.backToList') }}</a>
          <h3>{{ versionLabel(version()!.versionNumber) }}</h3>
          <p>{{ t('insurance.productVersions.fields.status') }}: {{ version()!.status }}</p>
          <div class="actions toolbar-actions">
            <button pButton type="button" severity="secondary" [label]="t('insurance.productVersions.actions.validate')" (click)="validateVersion()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.productVersions.actions.export')" (click)="exportBundle()"></button>
          </div>
          @if (validationErrors().length > 0) {
            <ul class="error-list">
              @for (item of validationErrors(); track item) {
                <li>{{ item }}</li>
              }
            </ul>
          }
          <form class="grid" (ngSubmit)="saveEdit()">
            <label>
              <span>{{ t('insurance.productVersions.fields.minPremium') }}</span>
              <p-inputNumber [(ngModel)]="editForm.minPremium" [min]="0" name="minPremium"></p-inputNumber>
            </label>
            <label>
              <span>{{ t('insurance.productVersions.fields.maxSumInsured') }}</span>
              <p-inputNumber [(ngModel)]="editForm.maxSumInsured" [min]="0" name="maxSumInsured"></p-inputNumber>
            </label>
            <label>
              <span>{{ t('insurance.productVersions.fields.uwFormDefinitionId') }}</span>
              <p-select [options]="uwFormOptions()" optionLabel="label" optionValue="value" [(ngModel)]="editForm.uwFormDefinitionId" name="editUwFormDefinitionId" appendTo="body"></p-select>
            </label>
            <label class="wide">
              <span>{{ t('insurance.productVersions.fields.terms') }}</span>
              <div class="terms-grid">
                <label>
                  <span>{{ t('insurance.productVersions.fields.termsTitle') }}</span>
                  <input pInputText [(ngModel)]="editTermsForm.title" name="editTermsTitle" />
                </label>
                <label>
                  <span>{{ t('insurance.productVersions.fields.waitingPeriodDays') }}</span>
                  <p-inputNumber [(ngModel)]="editTermsForm.waitingPeriodDays" [min]="0" name="editWaitingPeriodDays"></p-inputNumber>
                </label>
                <label class="wide">
                  <span>{{ t('insurance.productVersions.fields.termsSummary') }}</span>
                  <textarea pTextarea rows="3" [(ngModel)]="editTermsForm.summary" name="editTermsSummary"></textarea>
                </label>
                <label class="wide">
                  <span>{{ t('insurance.productVersions.fields.cancellationPolicy') }}</span>
                  <textarea pTextarea rows="3" [(ngModel)]="editTermsForm.cancellationPolicy" name="editCancellationPolicy"></textarea>
                </label>
                <label class="wide">
                  <span>{{ t('insurance.productVersions.fields.exclusions') }}</span>
                  <textarea pTextarea rows="3" [(ngModel)]="editTermsForm.exclusions" name="editExclusions"></textarea>
                </label>
              </div>
            </label>
            <div class="actions">
              <button pButton type="submit" [label]="t('common.save')" [disabled]="saving() || version()!.status !== 'Draft'"></button>
              <button pButton type="button" severity="secondary" [label]="t('insurance.productVersions.actions.publish')" [disabled]="version()!.status !== 'Draft'" (click)="publish()"></button>
              <button pButton type="button" severity="secondary" [label]="t('insurance.productVersions.actions.retire')" [disabled]="version()!.status !== 'Published'" (click)="retire()"></button>
            </div>
          </form>
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'summary'" (click)="activeTab.set('summary')">{{ t('insurance.productVersionDetail.tabs.summary') }}</button>
            <button type="button" [class.active]="activeTab() === 'coverages'" (click)="activeTab.set('coverages')">{{ t('insurance.productVersionDetail.tabs.coverages') }}</button>
            <button type="button" [class.active]="activeTab() === 'clauses'" (click)="activeTab.set('clauses')">{{ t('insurance.productVersionDetail.tabs.clauses') }}</button>
            <button type="button" [class.active]="activeTab() === 'questions'" (click)="activeTab.set('questions')">{{ t('insurance.productVersionDetail.tabs.questions') }}</button>
            <button type="button" [class.active]="activeTab() === 'uw'" (click)="activeTab.set('uw')">{{ t('insurance.productVersionDetail.tabs.uw') }}</button>
          </div>
          @if (activeTab() === 'coverages') {
            <section class="card nested-card">
              <div class="actions">
                <button pButton type="button" [label]="t('insurance.productVersions.coverages.add')" (click)="addCoverageRow()"></button>
                <button pButton type="button" severity="secondary" [label]="t('common.save')" (click)="saveCoverages()" [disabled]="version()!.status !== 'Draft'"></button>
              </div>
              @for (item of coverages(); track item.localId) {
                <div class="sub-grid row-card">
                  <label><span>{{ t('insurance.productVersions.coverages.code') }}</span><input pInputText [(ngModel)]="item.code" [ngModelOptions]="{standalone: true}" /></label>
                  <label><span>{{ t('insurance.productVersions.coverages.name') }}</span><input pInputText [(ngModel)]="item.name" [ngModelOptions]="{standalone: true}" /></label>
                  <label>
                    <span>{{ t('insurance.productVersions.coverages.type') }}</span>
                    <p-select [options]="coverageTypes" optionLabel="label" optionValue="value" [(ngModel)]="item.coverageType" [ngModelOptions]="{standalone: true}" appendTo="body"></p-select>
                  </label>
                  <label><span>{{ t('insurance.productVersions.coverages.defaultSumInsured') }}</span><p-inputNumber [(ngModel)]="item.defaultSumInsured" [ngModelOptions]="{standalone: true}" /></label>
                  <label><span>{{ t('insurance.productVersions.coverages.minSumInsured') }}</span><p-inputNumber [(ngModel)]="item.minSumInsured" [ngModelOptions]="{standalone: true}" /></label>
                  <label><span>{{ t('insurance.productVersions.coverages.maxSumInsured') }}</span><p-inputNumber [(ngModel)]="item.maxSumInsured" [ngModelOptions]="{standalone: true}" /></label>
                  <label><span>{{ t('insurance.productVersions.coverages.defaultSelected') }}</span><p-checkbox [(ngModel)]="item.isDefaultSelected" [ngModelOptions]="{standalone: true}" [binary]="true"></p-checkbox></label>
                </div>
              }
            </section>
          }
          @if (activeTab() === 'clauses') {
            <section class="card nested-card">
              <div class="actions">
                <button pButton type="button" [label]="t('insurance.productVersions.clauses.add')" (click)="addClauseRow()"></button>
                <button pButton type="button" severity="secondary" [label]="t('common.save')" (click)="saveClauses()" [disabled]="version()!.status !== 'Draft'"></button>
              </div>
              @for (item of clauses(); track item.localId) {
                <div class="sub-grid row-card">
                  <label><span>{{ t('insurance.productVersions.clauses.code') }}</span><input pInputText [(ngModel)]="item.clauseCode" [ngModelOptions]="{standalone: true}" /></label>
                  <label><span>{{ t('insurance.productVersions.clauses.title') }}</span><input pInputText [(ngModel)]="item.title" [ngModelOptions]="{standalone: true}" /></label>
                  <label>
                    <span>{{ t('insurance.productVersions.clauses.type') }}</span>
                    <p-select [options]="clauseTypes" optionLabel="label" optionValue="value" [(ngModel)]="item.clauseType" [ngModelOptions]="{standalone: true}" appendTo="body"></p-select>
                  </label>
                  <label class="wide"><span>{{ t('insurance.productVersions.clauses.body') }}</span><textarea pTextarea rows="3" [(ngModel)]="item.bodyText" [ngModelOptions]="{standalone: true}"></textarea></label>
                </div>
              }
            </section>
          }
          @if (activeTab() === 'questions') {
            <section class="card nested-card">
              <div class="actions">
                <button pButton type="button" [label]="t('insurance.productVersions.questions.add')" (click)="addQuestionRow()"></button>
                <button pButton type="button" severity="secondary" [label]="t('common.save')" (click)="saveQuestions()" [disabled]="version()!.status !== 'Draft'"></button>
              </div>
              @for (item of questions(); track item.localId) {
                <div class="sub-grid row-card">
                  <label><span>{{ t('insurance.productVersions.questions.key') }}</span><input pInputText [(ngModel)]="item.questionKey" [ngModelOptions]="{standalone: true}" /></label>
                  <label>
                    <span>{{ t('insurance.productVersions.questions.dataType') }}</span>
                    <p-select [options]="questionDataTypes" optionLabel="label" optionValue="value" [(ngModel)]="item.dataType" [ngModelOptions]="{standalone: true}" appendTo="body"></p-select>
                  </label>
                  <label><span>{{ t('insurance.productVersions.questions.required') }}</span><p-checkbox [(ngModel)]="item.required" [ngModelOptions]="{standalone: true}" [binary]="true"></p-checkbox></label>
                  <label><span>{{ t('insurance.productVersions.questions.ratingFactor') }}</span><input pInputText [(ngModel)]="item.mapsToRatingFactor" [ngModelOptions]="{standalone: true}" /></label>
                  <label class="wide"><span>{{ t('insurance.productVersions.questions.validationJson') }}</span><textarea pTextarea rows="3" [(ngModel)]="item.validationJson" [ngModelOptions]="{standalone: true}"></textarea></label>
                </div>
              }
            </section>
          }
          @if (activeTab() === 'uw') {
            <section class="card nested-card">
              <div class="actions">
                <button pButton type="button" [label]="t('insurance.productVersions.uwRules.add')" (click)="addUwRuleRow()"></button>
                <button pButton type="button" severity="secondary" [label]="t('common.save')" (click)="saveUwRules()" [disabled]="version()!.status !== 'Draft'"></button>
              </div>
              @for (item of uwRules(); track item.localId) {
                <div class="sub-grid row-card">
                  <label><span>{{ t('insurance.productVersions.uwRules.priority') }}</span><p-inputNumber [(ngModel)]="item.priority" [ngModelOptions]="{standalone: true}" [min]="0"></p-inputNumber></label>
                  <label>
                    <span>{{ t('insurance.productVersions.uwRules.action') }}</span>
                    <p-select [options]="uwActions" optionLabel="label" optionValue="value" [(ngModel)]="item.action" [ngModelOptions]="{standalone: true}" appendTo="body"></p-select>
                  </label>
                  <label><span>{{ t('insurance.productVersions.uwRules.maxSumInsured') }}</span><p-inputNumber [(ngModel)]="item.maxSumInsured" [ngModelOptions]="{standalone: true}" [min]="0"></p-inputNumber></label>
                  <label>
                    <span>{{ t('insurance.productVersions.uwRules.paymentPlanPreference') }}</span>
                    <p-select [options]="uwPaymentPlans" optionLabel="label" optionValue="value" [(ngModel)]="item.paymentPlanPreference" [ngModelOptions]="{standalone: true}" appendTo="body"></p-select>
                  </label>
                  <label>
                    <span>{{ t('insurance.productVersions.uwRules.hasBeneficiaries') }}</span>
                    <p-select [options]="uwTriStateOptions" optionLabel="label" optionValue="value" [(ngModel)]="item.hasBeneficiaries" [ngModelOptions]="{standalone: true}" appendTo="body"></p-select>
                  </label>
                  <label>
                    <span>{{ t('insurance.productVersions.uwRules.hasInsuredParties') }}</span>
                    <p-select [options]="uwTriStateOptions" optionLabel="label" optionValue="value" [(ngModel)]="item.hasInsuredParties" [ngModelOptions]="{standalone: true}" appendTo="body"></p-select>
                  </label>
                  <label>
                    <span>{{ t('insurance.productVersions.uwRules.minBeneficiariesCount') }}</span>
                    <p-inputNumber [(ngModel)]="item.minBeneficiariesCount" [ngModelOptions]="{standalone: true}" [min]="0"></p-inputNumber>
                  </label>
                  <label>
                    <span>{{ t('insurance.productVersions.uwRules.minInsuredPartiesCount') }}</span>
                    <p-inputNumber [(ngModel)]="item.minInsuredPartiesCount" [ngModelOptions]="{standalone: true}" [min]="0"></p-inputNumber>
                  </label>
                  <label class="wide">
                    <span>{{ t('insurance.productVersions.uwRules.zoneCodes') }}</span>
                    <input pInputText [(ngModel)]="item.zoneCodesText" [ngModelOptions]="{standalone: true}" [placeholder]="t('insurance.productVersions.uwRules.zoneCodesPlaceholder')" />
                  </label>
                  @if ((uwRuleErrors()[item.localId]?.length ?? 0) > 0) {
                    <ul class="error-list wide">
                      @for (message of uwRuleErrors()[item.localId]; track message) {
                        <li>{{ message }}</li>
                      }
                    </ul>
                  }
                  <p class="wide hint">{{ t('insurance.productVersions.uwRules.builderHint') }}</p>
                  <label class="wide"><span>{{ t('insurance.productVersions.uwRules.conditionJson') }}</span><textarea pTextarea rows="3" [ngModel]="previewConditionJson(item)" [ngModelOptions]="{standalone: true}" readonly></textarea></label>
                  <label class="wide"><span>{{ t('insurance.productVersions.uwRules.zoneCodesJson') }}</span><textarea pTextarea rows="2" [ngModel]="previewZoneCodesJson(item)" [ngModelOptions]="{standalone: true}" readonly></textarea></label>
                </div>
              }
            </section>
          }
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card{border:1px solid #e5e7eb;border-radius:12px;padding:12px;margin-bottom:10px}
    .back-link{color:#1d4ed8;font-weight:600;text-decoration:none}
    .grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}
    label{display:grid;gap:6px}
    .wide{grid-column:1 / -1}
    .terms-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}
    .terms-grid textarea{width:100%}
    .tabs{display:flex;flex-wrap:wrap;gap:8px;border-bottom:1px solid #e2e8f0;padding:8px 0;margin-top:16px}
    .tabs button{border:0;background:transparent;padding:6px 10px;border-radius:8px;cursor:pointer}
    .tabs button.active{background:#dbeafe;color:#1e40af;font-weight:600}
    .nested-card{margin-top:10px}
    .toolbar-actions{margin:10px 0}
    .sub-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}
    .row-card{border:1px solid #e5e7eb;border-radius:8px;padding:8px;margin-top:8px}
    .hint{font-size:.85rem;color:#6b7280;margin:0}
    .error-list{color:#b91c1c;margin:8px 0;padding-left:18px}
    .actions{display:flex;gap:8px;align-items:center}
    .error{color:#b91c1c}
    @media (max-width: 760px){ .grid,.terms-grid,.sub-grid{grid-template-columns:1fr} }
  `]
})
export class InsuranceProductVersionDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(InsuranceApiService);
  private readonly dynamicApi = inject(DynamicApiService);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly isCreateMode = signal(false);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly version = signal<InsuranceProductVersionDetail | null>(null);
  readonly productId = signal('');
  readonly validationErrors = signal<string[]>([]);
  readonly uwRuleErrors = signal<Record<string, string[]>>({});
  readonly uwFormOptions = signal<Array<{ label: string; value: string | null }>>([]);
  readonly activeTab = signal<'summary' | 'coverages' | 'clauses' | 'questions' | 'uw'>('summary');
  readonly coverages = signal<CoverageFormRow[]>([]);
  readonly clauses = signal<ClauseFormRow[]>([]);
  readonly questions = signal<QuestionFormRow[]>([]);
  readonly uwRules = signal<UwRuleFormRow[]>([]);
  readonly coverageTypes = [
    { label: 'Mandatory', value: 'Mandatory' },
    { label: 'Optional', value: 'Optional' },
    { label: 'Rider', value: 'Rider' }
  ];
  readonly clauseTypes = [
    { label: 'Inclusion', value: 'Inclusion' },
    { label: 'Exclusion', value: 'Exclusion' },
    { label: 'Condition', value: 'Condition' }
  ];
  readonly questionDataTypes = [
    { label: 'Text', value: 'Text' },
    { label: 'Number', value: 'Number' },
    { label: 'Boolean', value: 'Boolean' },
    { label: 'Date', value: 'Date' },
    { label: 'Select', value: 'Select' }
  ];
  readonly uwActions = [
    { label: 'AutoApprove', value: 'AutoApprove' },
    { label: 'AutoDecline', value: 'AutoDecline' },
    { label: 'Refer', value: 'Refer' },
    { label: 'RequireInfo', value: 'RequireInfo' }
  ];
  readonly uwPaymentPlans = [
    { label: this.t('insurance.productVersions.uwRules.anyValue'), value: null as string | null },
    { label: 'monthly', value: 'monthly' },
    { label: 'quarterly', value: 'quarterly' },
    { label: 'yearly', value: 'yearly' }
  ];
  readonly uwTriStateOptions = [
    { label: this.t('insurance.productVersions.uwRules.anyValue'), value: null as boolean | null },
    { label: this.t('common.yes'), value: true },
    { label: this.t('common.no'), value: false }
  ];
  createTermsForm = this.emptyTermsForm();
  editTermsForm = this.emptyTermsForm();

  createForm: CreateInsuranceProductVersionPayload = {
    legalEntityId: '',
    versionNumber: 1,
    termsAndConditionsJson: '{}',
    minPremium: null,
    maxSumInsured: null,
    riskSchemaId: null,
    uwFormDefinitionId: null
  };

  editForm: UpdateInsuranceProductVersionPayload = {
    termsAndConditionsJson: '{}',
    minPremium: null,
    maxSumInsured: null,
    riskSchemaId: null,
    uwFormDefinitionId: null
  };

  ngOnInit(): void {
    const productId = this.route.snapshot.paramMap.get('productId');
    const versionId = this.route.snapshot.paramMap.get('versionId');
    const isNewRoute = this.route.snapshot.url.at(-1)?.path === 'new';

    if (!productId) {
      this.loading.set(false);
      return;
    }

    this.productId.set(productId);
    this.loadUwFormOptions();

    if (isNewRoute) {
      this.isCreateMode.set(true);
      this.api.getProduct(productId).subscribe({
        next: product => {
          if (product?.legalEntityId) {
            this.createForm.legalEntityId = product.legalEntityId;
          }
          this.createTermsForm = this.parseTerms(this.createForm.termsAndConditionsJson);
          this.loading.set(false);
        },
        error: () => {
          this.createTermsForm = this.parseTerms(this.createForm.termsAndConditionsJson);
          this.loading.set(false);
        }
      });
      return;
    }

    if (!versionId) {
      this.loading.set(false);
      return;
    }

    this.api.getProductVersion(productId, versionId).subscribe({
      next: item => {
        this.version.set(item);
        if (item) {
          this.editForm = {
            termsAndConditionsJson: item.termsAndConditionsJson,
            minPremium: item.minPremium,
            maxSumInsured: item.maxSumInsured,
            riskSchemaId: item.riskSchemaId,
            uwFormDefinitionId: item.uwFormDefinitionId
          };
          this.editTermsForm = this.parseTerms(item.termsAndConditionsJson);
          this.loadVersionStudio();
        }
        this.loading.set(false);
      },
      error: () => {
        this.version.set(null);
        this.loading.set(false);
      }
    });
  }

  saveCreate(): void {
    this.error.set('');
    this.saving.set(true);
    this.createForm.termsAndConditionsJson = this.serializeTerms(this.createTermsForm);
    this.api.createProductVersion(this.productId(), this.createForm).subscribe({
      next: item => {
        this.saving.set(false);
        void this.router.navigate(['/insurance/products', this.productId(), 'versions', item.id]);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  saveEdit(): void {
    const current = this.version();
    if (!current) {
      return;
    }

    this.error.set('');
    this.saving.set(true);
    this.editForm.termsAndConditionsJson = this.serializeTerms(this.editTermsForm);
    this.api.updateProductVersion(this.productId(), current.id, this.editForm).subscribe({
      next: item => {
        this.version.set(item);
        this.loadVersionStudio();
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  publish(): void {
    const current = this.version();
    if (!current) {
      return;
    }

    this.api.publishProductVersion(this.productId(), current.id).subscribe({
      next: item => this.version.set(item),
      error: () => this.error.set(this.t('insurance.states.loadError'))
    });
  }

  retire(): void {
    const current = this.version();
    if (!current) {
      return;
    }

    this.api.retireProductVersion(this.productId(), current.id).subscribe({
      next: item => this.version.set(item),
      error: () => this.error.set(this.t('insurance.states.loadError'))
    });
  }

  versionLabel(versionNumber: number): string {
    return `${this.t('insurance.productVersions.item')} ${versionNumber}`;
  }

  addCoverageRow(): void {
    this.coverages.update(rows => [...rows, this.emptyCoverageRow()]);
  }

  saveCoverages(): void {
    const current = this.version();
    if (!current) {
      return;
    }
    this.api.upsertVersionCoverages(this.productId(), current.id, {
      items: this.coverages().map(item => ({
        code: item.code.trim(),
        name: item.name.trim(),
        coverageType: item.coverageType,
        defaultSumInsured: item.defaultSumInsured,
        minSumInsured: item.minSumInsured,
        maxSumInsured: item.maxSumInsured,
        isDefaultSelected: item.isDefaultSelected
      }))
    }).subscribe({
      next: rows => this.coverages.set(rows.map(item => this.toCoverageRow(item))),
      error: () => this.error.set(this.t('insurance.states.loadError'))
    });
  }

  addClauseRow(): void {
    this.clauses.update(rows => [...rows, this.emptyClauseRow()]);
  }

  saveClauses(): void {
    const current = this.version();
    if (!current) {
      return;
    }
    this.api.upsertVersionClauses(this.productId(), current.id, {
      items: this.clauses().map(item => ({
        clauseCode: item.clauseCode.trim(),
        title: item.title.trim(),
        bodyText: item.bodyText.trim(),
        clauseType: item.clauseType,
        appliesToCoverageId: null
      }))
    }).subscribe({
      next: rows => this.clauses.set(rows.map(item => this.toClauseRow(item))),
      error: () => this.error.set(this.t('insurance.states.loadError'))
    });
  }

  addQuestionRow(): void {
    this.questions.update(rows => [...rows, this.emptyQuestionRow()]);
  }

  saveQuestions(): void {
    const current = this.version();
    if (!current) {
      return;
    }
    this.api.upsertVersionQuestions(this.productId(), current.id, {
      items: this.questions().map(item => ({
        questionKey: item.questionKey.trim(),
        dataType: item.dataType,
        required: item.required,
        validationJson: item.validationJson.trim() || '{}',
        mapsToRatingFactor: item.mapsToRatingFactor?.trim() || null
      }))
    }).subscribe({
      next: rows => this.questions.set(rows.map(item => this.toQuestionRow(item))),
      error: () => this.error.set(this.t('insurance.states.loadError'))
    });
  }

  addUwRuleRow(): void {
    this.uwRules.update(rows => [...rows, this.emptyUwRuleRow()]);
    this.uwRuleErrors.set({});
  }

  saveUwRules(): void {
    const current = this.version();
    if (!current) {
      return;
    }

    const validation = this.validateUwRules(this.uwRules());
    this.uwRuleErrors.set(validation.errorsById);
    if (validation.hasAnyError) {
      this.error.set(this.t('insurance.productVersions.uwRules.validationFailed'));
      return;
    }
    this.error.set('');

    const items: PutInsuranceUwRuleItemPayload[] = this.uwRules().map(item => ({
      priority: item.priority,
      action: item.action,
      maxSumInsured: item.maxSumInsured,
      conditionJson: this.buildUwConditionJson(item),
      zoneCodesJson: this.buildUwZoneCodesJson(item)
    }));
    this.api.putUwRules(this.productId(), current.id, items).subscribe({
      next: rows => {
        this.uwRules.set(rows.map(item => this.toUwRuleRow(item)));
        this.uwRuleErrors.set({});
      },
      error: () => this.error.set(this.t('insurance.states.loadError'))
    });
  }

  validateVersion(): void {
    const current = this.version();
    if (!current) {
      return;
    }
    this.api.validateProductVersion(this.productId(), current.id).subscribe({
      next: result => this.validationErrors.set(result.errors),
      error: () => this.error.set(this.t('insurance.states.loadError'))
    });
  }

  exportBundle(): void {
    const current = this.version();
    if (!current) {
      return;
    }
    this.api.exportProductVersion(this.productId(), current.id).subscribe({
      next: bundle => {
        const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `insurance-product-version-${current.versionNumber}.json`;
        link.click();
        URL.revokeObjectURL(url);
      },
      error: () => this.error.set(this.t('insurance.states.loadError'))
    });
  }

  createCanSave(): boolean {
    return !!(this.createForm.legalEntityId.trim() && this.createForm.versionNumber > 0);
  }

  private serializeTerms(form: TermsForm): string {
    return JSON.stringify({
      title: form.title.trim(),
      summary: form.summary.trim(),
      cancellationPolicy: form.cancellationPolicy.trim(),
      exclusions: form.exclusions.trim(),
      waitingPeriodDays: form.waitingPeriodDays
    });
  }

  private parseTerms(rawJson: string): TermsForm {
    try {
      const parsed = JSON.parse(rawJson) as {
        title?: string;
        summary?: string;
        cancellationPolicy?: string;
        exclusions?: string;
        waitingPeriodDays?: number | null;
      };

      return {
        title: parsed.title ?? '',
        summary: parsed.summary ?? '',
        cancellationPolicy: parsed.cancellationPolicy ?? '',
        exclusions: parsed.exclusions ?? '',
        waitingPeriodDays: typeof parsed.waitingPeriodDays === 'number' ? parsed.waitingPeriodDays : null
      };
    } catch {
      return this.emptyTermsForm();
    }
  }

  private emptyTermsForm(): TermsForm {
    return {
      title: '',
      summary: '',
      cancellationPolicy: '',
      exclusions: '',
      waitingPeriodDays: null
    };
  }

  private loadUwFormOptions(): void {
    this.dynamicApi.listForms().subscribe({
      next: forms => this.uwFormOptions.set(this.mapUwFormOptions(forms)),
      error: () => this.uwFormOptions.set([{ label: this.t('common.none'), value: null }])
    });
  }

  private mapUwFormOptions(forms: DynamicForm[]): Array<{ label: string; value: string | null }> {
    const options = forms
      .map(item => ({ label: item.name, value: item.id }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [{ label: this.t('common.none'), value: null }, ...options];
  }

  private loadVersionStudio(): void {
    const current = this.version();
    if (!current) {
      return;
    }
    this.api.listVersionCoverages(this.productId(), current.id).subscribe({
      next: rows => this.coverages.set(rows.map(item => this.toCoverageRow(item))),
      error: () => this.coverages.set([])
    });
    this.api.listVersionClauses(this.productId(), current.id).subscribe({
      next: rows => this.clauses.set(rows.map(item => this.toClauseRow(item))),
      error: () => this.clauses.set([])
    });
    this.api.listVersionQuestions(this.productId(), current.id).subscribe({
      next: rows => this.questions.set(rows.map(item => this.toQuestionRow(item))),
      error: () => this.questions.set([])
    });
    this.api.listUwRules(this.productId(), current.id).subscribe({
      next: rows => this.uwRules.set(rows.map(item => this.toUwRuleRow(item))),
      error: () => this.uwRules.set([])
    });
  }

  private emptyCoverageRow(): CoverageFormRow {
    return {
      localId: crypto.randomUUID(),
      code: '',
      name: '',
      coverageType: 'Optional',
      defaultSumInsured: null,
      minSumInsured: null,
      maxSumInsured: null,
      isDefaultSelected: false
    };
  }

  private emptyClauseRow(): ClauseFormRow {
    return {
      localId: crypto.randomUUID(),
      clauseCode: '',
      title: '',
      bodyText: '',
      clauseType: 'Inclusion'
    };
  }

  private emptyQuestionRow(): QuestionFormRow {
    return {
      localId: crypto.randomUUID(),
      questionKey: '',
      dataType: 'Text',
      required: false,
      validationJson: '{}',
      mapsToRatingFactor: null
    };
  }

  private emptyUwRuleRow(): UwRuleFormRow {
    return {
      localId: crypto.randomUUID(),
      priority: 0,
      action: 'AutoApprove',
      maxSumInsured: null,
      conditionJson: '{}',
      zoneCodesJson: '[]',
      paymentPlanPreference: null,
      hasBeneficiaries: null,
      hasInsuredParties: null,
      minBeneficiariesCount: null,
      minInsuredPartiesCount: null,
      zoneCodesText: ''
    };
  }

  private toCoverageRow(item: InsuranceCoverageItem): CoverageFormRow {
    return {
      localId: item.id,
      code: item.code,
      name: item.name,
      coverageType: item.coverageType,
      defaultSumInsured: item.defaultSumInsured,
      minSumInsured: item.minSumInsured,
      maxSumInsured: item.maxSumInsured,
      isDefaultSelected: item.isDefaultSelected
    };
  }

  private toClauseRow(item: InsuranceClauseItem): ClauseFormRow {
    return {
      localId: item.id,
      clauseCode: item.clauseCode,
      title: item.title,
      bodyText: item.bodyText,
      clauseType: item.clauseType
    };
  }

  private toQuestionRow(item: InsuranceQuestionItem): QuestionFormRow {
    return {
      localId: item.id,
      questionKey: item.questionKey,
      dataType: item.dataType,
      required: item.required,
      validationJson: item.validationJson,
      mapsToRatingFactor: item.mapsToRatingFactor
    };
  }

  private toUwRuleRow(item: InsuranceUwRule): UwRuleFormRow {
    const condition = this.parseUwCondition(item.conditionJson);
    const zoneCodes = this.parseUwZoneCodes(item.zoneCodesJson);
    return {
      localId: item.id,
      priority: item.priority,
      action: item.action,
      maxSumInsured: item.maxSumInsured,
      conditionJson: item.conditionJson,
      zoneCodesJson: item.zoneCodesJson,
      paymentPlanPreference: condition.paymentPlanPreference,
      hasBeneficiaries: condition.hasBeneficiaries,
      hasInsuredParties: condition.hasInsuredParties,
      minBeneficiariesCount: condition.minBeneficiariesCount,
      minInsuredPartiesCount: condition.minInsuredPartiesCount,
      zoneCodesText: zoneCodes.join(', ')
    };
  }

  previewConditionJson(item: UwRuleFormRow): string {
    return this.buildUwConditionJson(item);
  }

  previewZoneCodesJson(item: UwRuleFormRow): string {
    return this.buildUwZoneCodesJson(item);
  }

  private buildUwConditionJson(item: UwRuleFormRow): string {
    const payload: Record<string, unknown> = {};
    if (item.paymentPlanPreference) {
      payload['paymentPlanPreference'] = item.paymentPlanPreference;
    }
    if (item.hasBeneficiaries !== null) {
      payload['hasBeneficiaries'] = item.hasBeneficiaries;
    }
    if (item.hasInsuredParties !== null) {
      payload['hasInsuredParties'] = item.hasInsuredParties;
    }
    if (item.minBeneficiariesCount !== null && item.minBeneficiariesCount >= 0) {
      payload['minBeneficiariesCount'] = item.minBeneficiariesCount;
    }
    if (item.minInsuredPartiesCount !== null && item.minInsuredPartiesCount >= 0) {
      payload['minInsuredPartiesCount'] = item.minInsuredPartiesCount;
    }
    return JSON.stringify(payload);
  }

  private buildUwZoneCodesJson(item: UwRuleFormRow): string {
    const zoneCodes = this.extractZoneCodes(item.zoneCodesText).codes;
    return JSON.stringify(zoneCodes);
  }

  private validateUwRules(rows: UwRuleFormRow[]): { errorsById: Record<string, string[]>; hasAnyError: boolean } {
    const errorsById: Record<string, string[]> = {};
    let hasAnyError = false;
    for (const row of rows) {
      const rowErrors: string[] = [];
      if (!Number.isInteger(row.priority) || row.priority < 0) {
        rowErrors.push(this.t('insurance.productVersions.uwRules.errors.invalidPriority'));
      }
      if (row.minBeneficiariesCount !== null && row.minBeneficiariesCount < 0) {
        rowErrors.push(this.t('insurance.productVersions.uwRules.errors.invalidMinBeneficiariesCount'));
      }
      if (row.minInsuredPartiesCount !== null && row.minInsuredPartiesCount < 0) {
        rowErrors.push(this.t('insurance.productVersions.uwRules.errors.invalidMinInsuredPartiesCount'));
      }
      if (row.hasBeneficiaries === false && (row.minBeneficiariesCount ?? 0) > 0) {
        rowErrors.push(this.t('insurance.productVersions.uwRules.errors.inconsistentBeneficiaryRule'));
      }
      if (row.hasInsuredParties === false && (row.minInsuredPartiesCount ?? 0) > 0) {
        rowErrors.push(this.t('insurance.productVersions.uwRules.errors.inconsistentInsuredPartyRule'));
      }
      const zoneParse = this.extractZoneCodes(row.zoneCodesText);
      if (zoneParse.invalidCodes.length > 0) {
        rowErrors.push(this.t('insurance.productVersions.uwRules.errors.invalidZoneCodes'));
      }

      if (rowErrors.length > 0) {
        hasAnyError = true;
      }
      errorsById[row.localId] = rowErrors;
    }
    return { errorsById, hasAnyError };
  }

  private extractZoneCodes(raw: string): { codes: string[]; invalidCodes: string[] } {
    const all = raw
      .split(',')
      .map(code => code.trim())
      .filter(code => code.length > 0);
    const validPattern = /^[A-Za-z0-9_-]+$/;
    const codes = Array.from(new Set(all.filter(code => validPattern.test(code))));
    const invalidCodes = all.filter(code => !validPattern.test(code));
    return { codes, invalidCodes };
  }

  private parseUwCondition(raw: string): UwRuleConditionForm {
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      return {
        paymentPlanPreference: typeof parsed['paymentPlanPreference'] === 'string' ? parsed['paymentPlanPreference'] : null,
        hasBeneficiaries: typeof parsed['hasBeneficiaries'] === 'boolean' ? parsed['hasBeneficiaries'] : null,
        hasInsuredParties: typeof parsed['hasInsuredParties'] === 'boolean' ? parsed['hasInsuredParties'] : null,
        minBeneficiariesCount: typeof parsed['minBeneficiariesCount'] === 'number' ? parsed['minBeneficiariesCount'] : null,
        minInsuredPartiesCount: typeof parsed['minInsuredPartiesCount'] === 'number' ? parsed['minInsuredPartiesCount'] : null
      };
    } catch {
      return {
        paymentPlanPreference: null,
        hasBeneficiaries: null,
        hasInsuredParties: null,
        minBeneficiariesCount: null,
        minInsuredPartiesCount: null
      };
    }
  }

  private parseUwZoneCodes(raw: string): string[] {
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return [];
      }
      return parsed
        .filter((item): item is string => typeof item === 'string')
        .map(item => item.trim())
        .filter(item => item.length > 0);
    } catch {
      return [];
    }
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}

type TermsForm = {
  title: string;
  summary: string;
  cancellationPolicy: string;
  exclusions: string;
  waitingPeriodDays: number | null;
};

type CoverageFormRow = {
  localId: string;
  code: string;
  name: string;
  coverageType: string;
  defaultSumInsured: number | null;
  minSumInsured: number | null;
  maxSumInsured: number | null;
  isDefaultSelected: boolean;
};

type ClauseFormRow = {
  localId: string;
  clauseCode: string;
  title: string;
  bodyText: string;
  clauseType: string;
};

type QuestionFormRow = {
  localId: string;
  questionKey: string;
  dataType: string;
  required: boolean;
  validationJson: string;
  mapsToRatingFactor: string | null;
};

type UwRuleFormRow = {
  localId: string;
  priority: number;
  action: string;
  maxSumInsured: number | null;
  conditionJson: string;
  zoneCodesJson: string;
  paymentPlanPreference: string | null;
  hasBeneficiaries: boolean | null;
  hasInsuredParties: boolean | null;
  minBeneficiariesCount: number | null;
  minInsuredPartiesCount: number | null;
  zoneCodesText: string;
};

type UwRuleConditionForm = {
  paymentPlanPreference: string | null;
  hasBeneficiaries: boolean | null;
  hasInsuredParties: boolean | null;
  minBeneficiariesCount: number | null;
  minInsuredPartiesCount: number | null;
};
